<template>
  <!-- :style="getFixedWidth" -->
  <div class="print-sheet" ref="printSheet" :style="getFixedWidth" :class="getPrintMonochrome">
    <!-- TODO: header should go in its own class -->
    <div class="print-sheet-header" >
      <div class="psh-logo-holder" :class="getPLHOrientation">
        <div class="psh-logo">
          <img class="psh-logo-img" src="../../assets/svg/nelson_logo.svg" />
        </div>
        <div class="psh-title-holder">
          <div class="psh-title">PipePro {{ $version }}</div>
          <div class="psh-title-data">Sprinkler Precipitation Datasheet</div>
        </div>
      </div>
    </div>
    <!-- TODO: end -->
    <div class="print-sheet-holder">
      <PrintDataHeader
        :record="getRecord"
        :data="getPrintData(0)"
      />
      <div class="print-data-chart">
        <div class="print-sheet-left">
          <PrintInfoBlock
              :record="getRecord"
              title="Quick Reference"
              :data="getPrintData(1)"
          />
        </div>
        <div id="printSheetMiddle" class="print-sheet-middle" :class="getPSROrientation">
          <div class="print-sheet-chart-holder">
            <div class="print-top-border">
              <OffsetContainer/>
            </div>
            <div class="rotator-print-pressure-flow-container">
              <ChartAxisLabel side="left" :label="'Sprinkler Pressure (' + pressureUnit + ')'" class="rotator-print-cal"/>
              <div class="rotator-print-pressure-flow-chart">
                <PressureFlowChart :isPrint="true"/>
              </div>
              <ChartAxisLabel side="right" :label="'Sprinkler Flow (' + flowUnit + ')'" class="rotator-print-cal" />
            </div>
            <div class="rotator-print-pipe-slope-container">
              <div class="spacing-offset rotator-print-cal" >
                <LayoutInfo :side="'left'" v-if="showFlush"/>
              </div>
              <div class="rotator-print-pipe-layout">
                <ContextMenu />
                <LayoutSegmentHolder :type="'pipe'"  />
                <InletBar/>
                <PipeEditor :type="'print'"/>
                <LayoutSegmentHolder :type="'slope'"  />
              </div>
              <div class="spacing-offset  rotator-print-cal">
                <LayoutInfo :side="'right'" v-if="showFlush" />
              </div>
            </div>
          </div>
          </div>
        <div id="printSheetRight" class="print-sheet-right">
          <PrintLegBlock
              v-if="showFlush"
              :record="getLegs"
          />
        </div>

      </div>
      <div class="print-sheet-test-data-holder">
        <PrintTestData />
      </div>
    </div>
  </div>
</template>

<script>
import PrintInfoBlock from '../print/sheet_components/PrintInfoBlock.vue';
import PrintNotes from '../print/sheet_components/PrintNotes.vue';
import PrintTestData from '../print/sheet_components/PrintTestData.vue';
import PrintDataBlock from '../print/sheet_components/PrintDataBlock.vue';
import { nextTick } from 'vue';
import { usePrintStore } from '../../store/PrintStore';
import { useIdentityStore } from '../../store/IdentityStore';
import DeltaDisplay from '../../components/DeltaDisplay.vue';
import SprinklerSelection from '../../components/SprinklerSelection.vue';
import ChartAxisLabel from '../../components/layout/pressure_flow_chart/ChartAxisLabel.vue';
import PressureFlowChart from '../../components/layout/pressure_flow_chart/PressureFlowChart.vue';
import PipeEditor from '../../components/layout/pipe_editor/PipeEditor.vue';
import LayoutInfo from '../../components/layout/LayoutInfo.vue';
import LayoutSegmentHolder from '../../components/layout/LayoutSegmentHolder.vue';
import ContextMenu from '../../components/layout/ContextMenu.vue';
import { useInputStore } from '../../store/InputStore';
import { useSegmentStore } from '../../store/SegmentStore';
import * as UnitConversion from '@/services/UnitConversionService';
import { useSettingsStore } from '@/store/SettingsStore';
import PrintLegBlock from '../../components/print/sheet_components/PrintLegBlock.vue';
import PrintDataHeader from '../../components/print/sheet_components/PrintDataHeader.vue';
import InletBar from '@/components/layout/pipe_editor/InletBar.vue';
import OffsetContainer from '@/components/OffsetContainer.vue';

export default {
  name: 'PrintSheet',
  components: {
    OffsetContainer,
    ContextMenu,
    LayoutSegmentHolder,
    LayoutInfo,
    PipeEditor,
    PressureFlowChart,
    ChartAxisLabel,
    SprinklerSelection,
    DeltaDisplay,
    PrintDataBlock,
    PrintInfoBlock,
    PrintTestData,
    PrintNotes,
    PrintLegBlock,
    PrintDataHeader,
    InletBar
  },
  props: ['record', 'page'],
  data () {
    return {
      printSheet: null,
    };
  },
  mounted () {
    this.printSheet = this.$refs.printSheet;
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  },
  computed: {
    getPrintMonochrome () {
      return usePrintStore().getPrintMonochrome ? 'monochrome' : '';
    },
    showFlush () {
      return useSettingsStore().IsFlush;
    },
    orientation () {
      return usePrintStore().orientation;
    },
    size () {
      return usePrintStore().size;
    },
    initials () {
      const user = useIdentityStore().User;
      return user ? user.initials : '';
    },
    getPNOrientation () {
      return usePrintStore().getOrientation === 'portrait' ? 'print-sprinkler-holder-portrait' : '';
    },
    getPSROrientation () {
      return usePrintStore().getOrientation === 'portrait' ? 'print-sheet-right-portrait' : '';
    },
    getPLHOrientation () {
      return usePrintStore().getOrientation === 'portrait' ? 'psh-logo-holder-portrait' : '';
    },
    getFixedWidth () {
      let px = usePrintStore().getDimensions;
      return 'width:' + px.width
        + 'px;min-width:' + (px.width - 4)
        + 'px;height:' + px.height
        + 'px;min-height:' + px.height
        + 'px;transform-origin:top left; // ;transform: scale(1)';
    },
    getRecord () {
      let date = new Date();
      let user = useIdentityStore().User;
      user = user ? user.initials : '';
      const inputStore = useInputStore();
      const segmentStore = useSegmentStore();
      const settingsStore = useSettingsStore();
      let r = {
        date: date,
        client: inputStore.getInputDataFor('Client').value,
        jobNumber: inputStore.getJobNumber,
        jobDescription: inputStore.getJobDescription,
        designer: user,
        pressureVariation: inputStore.getCalcDataValue('pressureVariation'),
        pressureDifference: inputStore.getCalcDataValue('pressureDifference'),
        flowVariation: inputStore.getCalcDataValue('flowVariation'),
        flowDifference: inputStore.getCalcDataValue('flowDifference'),
        submainPosition: UnitConversion.FilterLength(segmentStore.getInletPosition, settingsStore.getLengthUnit).toFixed(2),
        inletPressure: UnitConversion.FilterPressure(segmentStore.getInletPressure, this.pressureUnit).toFixed(2),
        totalFlow: UnitConversion.FilterFlow(inputStore.getCalcData.totalFlow, this.flowUnit).toFixed(2)
      };
      return r;
    },
    getLegs () {
      let legs = {
        flow: {
          label: 'Flow',
          left: document.getElementById('left-flow').innerHTML + ' ' + document.getElementById('left-flow-unit').innerHTML,
          right: document.getElementById('right-flow').innerHTML + ' ' + document.getElementById('right-flow-unit').innerHTML
        },
        flush: {
          label: 'Flush',
          left: document.getElementById('left-flush').dataset.clamped,
          right: document.getElementById('right-flush').dataset.clamped
        },
        pressure: {
          label: 'Pressure',
          left: document.getElementById('left-pressure').innerHTML + ' ' + document.getElementById('left-pressure-unit').innerHTML,
          right: document.getElementById('right-pressure').innerHTML + ' ' + document.getElementById('right-pressure-unit').innerHTML
        },
        velocity: {
          label: 'Velocity',
          left: document.getElementById('left-velocity').innerHTML + ' ' + document.getElementById('left-velocity-unit').innerHTML,
          right: document.getElementById('right-velocity').innerHTML + ' ' + document.getElementById('right-velocity-unit').innerHTML
        },
        flushTime: {
          label: 'Flush Time',
          left: document.getElementById('left-flushTime').innerHTML + ' ' + document.getElementById('left-flushTime-unit').innerHTML,
          right: document.getElementById('right-flushTime').innerHTML + ' ' + document.getElementById('right-flushTime-unit').innerHTML
        }
      };
      return legs;
    },
    pressureUnit () {
      return useSettingsStore().getPressureUnit;
    },
    flowUnit () {
      return useSettingsStore().getFlowUnit;
    }
  },
  watch: {
    orientation () {
      nextTick(() => {
        this.handleResize();
      });
    },
    size () {
      nextTick(() => {
        this.handleResize();
      });
    },
  },

  methods: {
    getPrintData (num) {

      let printData = usePrintStore().getPrintDataAtIndex(num).data;

      // TODO: this can be cleaned up optimized by searching through getInputData to pull the corresponding property
      // client
      printData[0].value = useInputStore().getInputData[2].value;
      // job number
      printData[1].value = useInputStore().getInputData[1].value;
      // job description
      printData[3].value = useInputStore().getInputData[0].value;

      return printData;

    },
    handleResize () {
      let printDisplay = document.getElementById('printDisplay');
      if (!printDisplay) { return; }
      let printSheet = this.printSheet;
      let widthDifference = printDisplay.clientWidth / (printSheet.clientWidth + 4);
      let heightDifference = printDisplay.clientHeight / (printSheet.clientHeight + 4);
      let scaledPrintSheetW = (printSheet.clientWidth + 4) * heightDifference;
      let scaledPrintSheetH = (printSheet.clientHeight + 4) * widthDifference;
      let difX = printDisplay.clientWidth - scaledPrintSheetW;
      let difY = printDisplay.clientHeight - scaledPrintSheetH;
      if (widthDifference > 1) { widthDifference = 1; }
      if (difY < 0) { difY = 0; }
      if (usePrintStore().getOrientation === 'landscape') {
        if (scaledPrintSheetH > printDisplay.clientHeight) {
          printSheet.style.transform = 'scale(' + heightDifference + ')';
          usePrintStore().setPageScale(heightDifference);
        } else {
          printSheet.style.transform = 'scale(' + widthDifference + ')';
          usePrintStore().setPageScale(widthDifference);
        }
      } else {
        printSheet.style.transform = 'scale(' + heightDifference + ') translateX(' + difX + 'px)';
        usePrintStore().setPageScale(heightDifference);
      }
    },
  },
};
</script>

<style scoped>
.print-sheet-header {
  width: 100%;
  display: flex;
  flex:1;
  max-height: 47px;
}
.print-sheet-left {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-left: 20px;
}
.print-sheet-middle {
  flex: 6;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform-origin: top left;
/*  height: calc(100% * .7);*/
}
.print-sheet-right {
  height: fit-content;
  min-width: 200px;
  flex: 1;
}
.print-sheet-right-portrait {
  flex: 1;
}
.print-sheet-holder {
  height: 100%;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  flex: 1;
}
.print-sheet-test-data-holder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-height: 50px;
  position: absolute;
  bottom: 0;
}
.component-spacer {
  flex: 0.5;
}
.print-sprinkler-holder {
  flex: 4;
  max-height: 50%;
  display: flex;
  flex-direction: column;
}
.print-sprinkler-holder-portrait {
  max-height: none;
}
.print-notes-holder {
  flex: 4;
  max-height: 50%;
  display: flex;
  flex-direction: column;
}
.psh-title {
  font-weight: 600;
}
.psh-logo-holder {
  flex: 1;
  display: flex;
  align-items: flex-end;
}
.psh-logo-holder-portrait {
  flex: 1.5;
}
.psh-user-holder {
  flex: 1;
  padding-left: 20px;
  padding-top: 10px;
}
.psh-logo-img {
  width: 42px;
}
.psh-title-data {
  margin-top: -3px;
}
.psh-title-holder {
  display: flex;
  flex-direction: column;
  margin-left: 5px;
}
.psr-grid-holder {
  overflow: hidden;
  width: 100%;
  display: flex;
}
.print-sheet {
  border: 2px solid black;
  display: flex;
  flex-direction: column;
  transform: scale(0.9);
  overflow: hidden;
}
.wind-info-holder {
  margin-top: 25px;
  margin-bottom: 10px;
  display: flex;
  font-weight: 600;
  font-size: 90%;
  align-items: center;
}
.print-arrow {
  height: 15px;
  width: auto;
  margin-left: 10px;
  margin-right: 50px;
}
.wind-speed {
  margin-left: 5px;
  font-weight: 400 !important;
}
.print-sheet-left-top {
  display: flex;
}
.print-sheet-left-bottom {
  display: flex;
  flex: 1;
  overflow: hidden;
}
.print-info {
  gap: 10px;
}
.print-info-left {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.print-info-right {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.rotator-print-header {
  display: flex;
  flex: 1;
  max-height: 70px;
}
.rotator-print-pressure-flow-container {
  display: flex;
  flex: 4;
/*  max-height: 50%;*/
}
.rotator-print-pipe-slope-container {
  display: flex;
  height: 50%;
}
.rotator-print-cal {
  background-color: white;
  flex: 1;
}
.rotator-print-pressure-flow-chart {
  flex: 7;
}
.rotator-print-pipe-layout {
  flex: 7;
  display: flex;
  flex-direction: column;
  overflow: hidden;

}
.rp-dd {
  flex: 1;
}
.print-top-border {
  width: 100%;
/*  border-bottom: 1px solid black;*/
}
.print-data-chart {
  display: flex;
  flex:1;
  margin-top: 20px;
}

.print-sheet-chart-holder {
  transform: scale(.7);
  min-width: 1024px;
  max-width: 1024px;
  min-height: 768px;
  transform-origin: top left;
  height: fit-content;
  display: flex;
  flex:1;
  flex-direction: column;
  border: 1px solid black;
  pointer-events: none !important;
}
</style>

<style>
  .page-break {
    page-break-after: always;
    height: 0;
    display: block;
  }

@media print {
  .print-sheet {
    border: 2px solid transparent !important;
    transform: scale(1) !important;
  }
}
</style>

